<template>
  <div :class="wrapperClasses">
    <ul
      :class="listClasses"
      role="tablist"
    >
      <li
        v-for="tab in visibleTabs"
        :key="tab.value"
        :class="itemClasses"
        role="presentation"
      >
        <button
          :id="tabId(tab.value)"
          :class="tabClasses(tab)"
          type="button"
          role="tab"
          :aria-label="tab.label"
          :aria-selected="isActive(tab.value)"
          :tabindex="isActive(tab.value) ? 0 : -1"
          :disabled="tab.disabled === true"
          @click="selectTab(tab)"
          @keydown="onKeydown($event, tab)"
        >
          <slot
            :name="`tab-${tab.value}`"
            :tab="tab"
            :is-active="isActive(tab.value)"
          >
            {{ tab.label }}
          </slot>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, watch } from 'vue';

const VARIANTS = {
  button: {
    wrapper: 'mb-4 border-b border-gray-200 dark:border-gray-700',
    list: 'flex flex-wrap -mb-px text-sm font-medium text-center',
    item: 'me-0',
    base: 'inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg transition-colors',
    active: 'text-tuin-100 border-tuin-100',
    inactive: 'text-gray-500 border-gray-100'
  },
  link: {
    wrapper: '',
    list: 'flex flex-wrap -mb-px text-sm font-medium text-center',
    item: 'me-0',
    base: 'inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg transition-colors',
    active: 'text-tuin-100 border-tuin-100',
    inactive: 'text-gray-500 border-gray-10'
  }
};

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  // [{ value, label, disabled?, visible?, class? }] or { value: label }
  tabs: {
    type: [Array, Object],
    default: () => []
  },
  variant: {
    type: String,
    default: 'button'
  },
  id: {
    type: String,
    default: 'tabs'
  },
  wrapperClass: {
    type: String,
    default: ''
  },
  listClass: {
    type: String,
    default: ''
  },
  itemClass: {
    type: String,
    default: ''
  },
  activeClass: {
    type: [String, Array],
    default: ''
  },
  inactiveClass: {
    type: [String, Array],
    default: ''
  },
  keyboard: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const styles = computed(() => VARIANTS[props.variant] || VARIANTS.button);

const normalizedTabs = computed(() => {
  const source = Array.isArray(props.tabs)
    ? props.tabs
    : Object.entries(props.tabs).map(([value, tab]) => (
      tab !== null && typeof tab === 'object'
        ? { value, ...tab }
        : { value, label: tab }
    ));

  const seen = new Set();

  return source
    .filter((tab) => tab !== null && tab !== undefined)
    .map((tab) => (
      typeof tab === 'string' || typeof tab === 'number'
        ? { value: tab, label: String(tab) }
        : { ...tab }
    ))
    .filter((tab) => {
      if (tab.value === null || tab.value === undefined || seen.has(tab.value)) {
        return false;
      }
      seen.add(tab.value);
      return true;
    });
});

const visibleTabs = computed(() => normalizedTabs.value.filter((tab) => tab.visible !== false));

const enabledTabs = computed(() => visibleTabs.value.filter((tab) => tab.disabled !== true));

const wrapperClasses = computed(() => [styles.value.wrapper, props.wrapperClass].filter(Boolean));

const listClasses = computed(() => [styles.value.list, props.listClass].filter(Boolean));

const itemClasses = computed(() => [styles.value.item, props.itemClass].filter(Boolean));

function tabId(value) {
  return `${props.id}-tab-${String(value).replace(/[^\w-]/g, '-')}`;
}

function isActive(value) {
  return props.modelValue === value;
}

function tabClasses(tab) {
  return [
    styles.value.base,
    tab.class,
    isActive(tab.value)
      ? (props.activeClass || styles.value.active)
      : (props.inactiveClass || styles.value.inactive),
    tab.disabled === true ? 'opacity-50 cursor-not-allowed' : ''
  ].filter(Boolean);
}

function selectTab(tab) {
  if (tab.disabled === true || isActive(tab.value)) {
    return;
  }
  emit('update:modelValue', tab.value);
  emit('change', tab.value, tab);
}

function focusTab(value) {
  nextTick(() => {
    const element = document.getElementById(tabId(value));
    if (element) {
      element.focus();
    }
  });
}

function onKeydown(event, tab) {
  if (!props.keyboard || enabledTabs.value.length === 0) {
    return;
  }

  const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
  if (!keys.includes(event.key)) {
    return;
  }

  const currentIndex = enabledTabs.value.findIndex((item) => item.value === tab.value);
  if (currentIndex === -1) {
    return;
  }

  event.preventDefault();

  const total = enabledTabs.value.length;
  let nextIndex = currentIndex;

  if (event.key === 'ArrowRight') {
    nextIndex = (currentIndex + 1) % total;
  } else if (event.key === 'ArrowLeft') {
    nextIndex = (currentIndex - 1 + total) % total;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = total - 1;
  }

  const next = enabledTabs.value[nextIndex];
  selectTab(next);
  focusTab(next.value);
}

function ensureValidValue() {
  if (enabledTabs.value.some((tab) => tab.value === props.modelValue)) {
    return;
  }
  if (enabledTabs.value.length === 0) {
    return;
  }
  emit('update:modelValue', enabledTabs.value[0].value);
}

onMounted(ensureValidValue);
watch([enabledTabs, () => props.modelValue], ensureValidValue);

defineExpose({ selectTab, focusTab });
</script>
